import { useEffect } from 'react'

/**
 * Pins the app shell to the browser's *visual viewport*.
 *
 * On iOS every browser (Chrome included) runs on WebKit, where the layout
 * viewport — what 100vh/100dvh measure — does NOT shrink when the soft keyboard
 * opens; only the visual viewport does, and the page is scrolled so the focused
 * field stays visible. Sizing the shell to 100dvh therefore left it taller than
 * the visible area (dead space below the bottom bar) and, once an input was
 * focused, scrolled the shell into a blank region.
 *
 * Mirroring visualViewport.height into --app-height and its offsetTop into
 * --app-offset-top (consumed as a `position: fixed` height + top) keeps the
 * shell exactly over the visible region at all times: no dead space, and when
 * the keyboard opens the shell shrinks to the area above it with the input bar
 * resting on the keyboard. Listens to both resize (height changes) and scroll
 * (offset changes as WebKit scrolls the layout for the focused field). CSS
 * falls back to top:0 / 100dvh until this runs.
 */
export const useViewportHeight = () => {
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const root = document.documentElement
    const sync = () => {
      root.style.setProperty('--app-height', `${vv.height}px`)
      root.style.setProperty('--app-offset-top', `${vv.offsetTop}px`)
    }

    sync()
    vv.addEventListener('resize', sync)
    vv.addEventListener('scroll', sync)
    return () => {
      vv.removeEventListener('resize', sync)
      vv.removeEventListener('scroll', sync)
    }
  }, [])
}
